// filepath: resources/assets/front/js/components/slider.js

import Splide from '@splidejs/splide';

const sliderInstances = new Map();
const isDev = import.meta.env.DEV;

/**
 * Init lazy : Splide ne se monte que quand le slider devient visible
 */
export function initSlider(element = document) {
    const sliders = element.querySelectorAll('.splide.builder-slider:not([data-splide-initialized])');
    if (sliders.length === 0) return;

    // Browsers récents : on défère via IntersectionObserver
    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    mountSlider(entry.target);
                    observer.unobserve(entry.target);
                }
            });
        }, { rootMargin: '200px' });  // commence à init 200px avant que ce soit visible

        sliders.forEach(s => observer.observe(s));
    } else {
        // Fallback ancien browser : init direct
        sliders.forEach(mountSlider);
    }
}

function mountSlider(sliderElement) {
    if (sliderElement.hasAttribute('data-splide-initialized')) return;

    // ─── Read layout UNE FOIS, batché ────────────────────────────
    // On lit tout en un coup pour éviter les reflows multiples
    const computedHeight = parseFloat(window.getComputedStyle(sliderElement).height);

    // Parse options
    let options = {};
    if (sliderElement.hasAttribute('data-splide')) {
        try {
            options = JSON.parse(sliderElement.getAttribute('data-splide'));
        } catch (e) {
            if (isDev) console.error('[Slider] data-splide invalid:', e);
        }
    }

    const finalOptions = {
        type: 'loop',
        perPage: 1,
        autoplay: true,
        interval: 5000,
        speed: 600,
        arrows: true,
        pagination: true,
        pauseOnHover: true,
        keyboard: true,
        drag: true,
        lazyLoad: 'nearby',
        preloadPages: 1,
        ...options,
    };

    if (computedHeight > 0) {
        finalOptions.fixedHeight = computedHeight;
    }

    try {
        const splide = new Splide(sliderElement, finalOptions);
        splide.mount();
        sliderElement.setAttribute('data-splide-initialized', 'true');

        if (sliderElement.id) {
            sliderInstances.set(sliderElement.id, splide);
        }
    } catch (error) {
        console.error('[Slider] Mount error:', error);
    }
}

export function destroySlider(sliderId) {
    const instance = sliderInstances.get(sliderId);
    if (!instance) return;
    instance.destroy();
    sliderInstances.delete(sliderId);
    const el = document.getElementById(sliderId);
    if (el) el.removeAttribute('data-splide-initialized');
}

export function getSliderInstance(sliderId) {
    return sliderInstances.get(sliderId) || null;
}

export function destroyAllSliders() {
    sliderInstances.forEach((instance) => instance.destroy());
    sliderInstances.clear();
}

// ─── Resize avec rAF + debounce (évite les reflows synchrones) ───
let resizeRAF = null;
let resizeTimeout = null;

window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
        if (resizeRAF) cancelAnimationFrame(resizeRAF);
        resizeRAF = requestAnimationFrame(() => {
            sliderInstances.forEach((instance, id) => {
                const el = document.getElementById(id);
                if (!el) return;
                const newHeight = parseFloat(window.getComputedStyle(el).height);
                if (newHeight > 0 && Math.abs(newHeight - instance.options.fixedHeight) > 10) {
                    instance.options.fixedHeight = newHeight;
                    instance.refresh();
                }
            });
        });
    }, 250);
}, { passive: true });

if (window.App) {
    window.App.slider = {
        init: initSlider,
        destroy: destroySlider,
        destroyAll: destroyAllSliders,
        getInstance: getSliderInstance,
    };
}